import { getEngineBroker, type EngineBroker } from './broker';
import type { FrameSubscriber, ProjectHandle } from './projectHandle';

export type AqueductSessionOptions = {
	apiUrl?: string;
	docId: string;
	clientId?: string;
	phoneResolutionPrompt?: boolean;
	reportLoadingProgress?: boolean;
	/** Disable only for precompiled/external surfaces or callers that request
	 * compiler readiness themselves before loading custom nodes. */
	warmSlangCompiler?: boolean;
};

/**
 * Scoped owner of one worker-side Aqueduct project instance.
 *
 * Product surfaces may compose the handle in very different ways, but none of
 * them should independently acquire, open, subscribe to, or close its engine
 * project. A session keeps that lifecycle paired and remains safe when several
 * independent Aqueduct instances share the page-wide worker.
 */
export class AqueductSession {
	readonly project: ProjectHandle;

	private readonly broker: EngineBroker;
	private readonly subscriptions = new Set<() => void>();
	private closed = false;

	constructor(options: AqueductSessionOptions) {
		this.broker = getEngineBroker();
		// Register readiness before any project work can load a document. The
		// worker shares the install and orders document loads behind it.
		if (options.warmSlangCompiler !== false) this.ensureSlangCompiler();
		this.project = this.broker.openProject({
			apiUrl: options.apiUrl ?? '',
			docId: options.docId,
			clientId: options.clientId ?? crypto.randomUUID(),
			phoneResolutionPrompt: options.phoneResolutionPrompt,
			reportLoadingProgress: options.reportLoadingProgress
		});
	}

	get booted(): Promise<void> {
		return this.broker.booted.then(() => undefined);
	}

	whenReady(): Promise<void> {
		return this.project.whenReady();
	}

	ensureSlangCompiler(): void {
		this.broker.ensureSlangCompiler();
	}

	subscribe(subscriber: FrameSubscriber): () => void {
		if (this.closed) return () => {};
		const unsubscribeProject = this.project.subscribe(subscriber);
		let active = true;
		const unsubscribe = () => {
			if (!active) return;
			active = false;
			unsubscribeProject();
			this.subscriptions.delete(unsubscribe);
		};
		this.subscriptions.add(unsubscribe);
		return unsubscribe;
	}

	loadGraphJson(contentJson: string): void {
		this.project.loadGraphJson(contentJson);
	}

	loadGraphToml(toml: string): void {
		this.project.loadGraphToml(toml);
	}

	connect(): void {
		this.project.connect();
	}

	setResolution(value: number, persistence: 'local' | 'persistent'): void {
		const resolution = Math.max(1, Math.floor(value));
		this.project.setUserResolution(resolution, persistence === 'persistent');
	}

	close(): void {
		if (this.closed) return;
		this.closed = true;
		for (const unsubscribe of Array.from(this.subscriptions)) unsubscribe();
		this.broker.closeProject(this.project);
	}
}
